import { useState, useCallback, useEffect, useMemo } from 'react';
import {
	parse,
	isValid,
	subMonths,
	addMonths,
	addDays,
	addWeeks,
	startOfMonth,
	endOfMonth,
	startOfDay,
	startOfWeek,
	endOfWeek,
	format,
	isBefore,
	isAfter,
	parseISO,
} from 'date-fns';
import { useCalendarData, useCalendarSettings, useCalendarConfig } from '../../../lib';
import { formatDateForInput } from '.';

const fetchMonthRange = async (targetDate: Date, fetch: (range: { start: string; end: string }) => Promise<void>) => {
	const start = format(startOfMonth(targetDate), 'yyyy-MM-dd');
	const end = format(endOfMonth(targetDate), 'yyyy-MM-dd');
	await fetch({ start, end });
};

export const useToolbarLogic = (date: Date, view: string, onNavigate: Function, onView: Function) => {
	const { settings } = useCalendarSettings();
	const { dateStart, dateEnd } = useCalendarConfig();
	const [inputValue, setInputValue] = useState<string>(() => formatDateForInput(date, view));
	const { fetchAllEventsInRange } = useCalendarData();

	// Parse date range boundaries
	const minDate = useMemo(() => (dateStart ? parseISO(dateStart) : null), [dateStart]);
	const maxDate = useMemo(() => (dateEnd ? parseISO(dateEnd) : null), [dateEnd]);

	useEffect(() => {
		setInputValue(formatDateForInput(date, view));
	}, [date, view]);

	const inputType = view === 'month' || view === 'agenda' ? 'month' : 'date';

	const handlePrevClick = useCallback(async () => {
		const twoMonthsAgo = subMonths(date, 2);
		await fetchMonthRange(twoMonthsAgo, fetchAllEventsInRange);
		onNavigate('PREV');
	}, [date, onNavigate, fetchAllEventsInRange]);

	const handleNextClick = useCallback(async () => {
		const twoMonthsAhead = addMonths(date, 2);
		await fetchMonthRange(twoMonthsAhead, fetchAllEventsInRange);
		onNavigate('NEXT');
	}, [date, onNavigate, fetchAllEventsInRange]);

	const handleTodayClick = useCallback(() => onNavigate('TODAY'), [onNavigate]);

	const handleDateInputChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			let newDate: Date | null = null;

			try {
				if (inputType === 'month') {
					newDate = parse(value, 'yyyy-MM', new Date());
				} else if (inputType === 'date') {
					newDate = parse(value, 'yyyy-MM-dd', new Date());
				}
			} catch (error) {
				console.error('Invalid date format', error);
			}

			if (newDate && isValid(newDate)) {
				await fetchMonthRange(newDate, fetchAllEventsInRange);
				onNavigate('DATE', newDate);
			}
		},
		[onNavigate, inputType, fetchAllEventsInRange]
	);

	const createViewChangeHandler = useCallback(
		(viewType: string) => () => {
			onView(viewType);
		},
		[onView]
	);

	const viewLabels: { [key: string]: string } = useMemo(
		() => ({
			month: settings.monthButtonLabel || 'Month',
			week: settings.weekButtonLabel || 'Week',
			day: settings.dayButtonLabel || 'Day',
			agenda: settings.agendaButtonLabel || 'Agenda',
		}),
		[settings]
	);

	// Calculate if navigation buttons should be disabled based on current view
	const isPrevDisabled = useMemo(() => {
		if (!minDate) return false;

		let prevDate: Date;
		if (view === 'day') {
			// Day view: check previous day
			prevDate = addDays(date, -1);
		} else if (view === 'week') {
			// Week view: check previous week (7 days)
			prevDate = addWeeks(date, -1);
		} else {
			// Month/Agenda view: check previous month
			prevDate = subMonths(date, 1);
		}

		// For month/agenda view, compare start of months
		// For day/week view, compare the actual dates
		if (view === 'month' || view === 'agenda') {
			return isBefore(startOfMonth(prevDate), startOfMonth(minDate));
		} else {
			return isBefore(prevDate, minDate);
		}
	}, [date, minDate, view]);

	const isNextDisabled = useMemo(() => {
		if (!maxDate) return false;

		let nextDate: Date;
		if (view === 'day') {
			// Day view: check next day
			nextDate = addDays(date, 1);
		} else if (view === 'week') {
			// Week view: check next week (7 days)
			nextDate = addWeeks(date, 1);
		} else {
			// Month/Agenda view: check next month
			nextDate = addMonths(date, 1);
		}

		// For month/agenda view, compare start of months
		// For day/week view, compare the actual dates
		if (view === 'month' || view === 'agenda') {
			return isAfter(startOfMonth(nextDate), startOfMonth(maxDate));
		} else {
			return isAfter(nextDate, maxDate);
		}
	}, [date, maxDate, view]);

	// Calculate if "Today/This Month" button should be disabled based on view type
	// - Month/Agenda: Compares current month against min/max month boundaries
	// - Week: Checks if current week overlaps with allowed date range
	// - Day: Compares dates only (ignoring time).
	const isTodayDisabled = useMemo(() => {
		const today = new Date();

		if (view === 'month' || view === 'agenda') {
			const currentMonth = startOfMonth(today);
			if (minDate && isBefore(currentMonth, startOfMonth(minDate))) return true;
			if (maxDate && isAfter(currentMonth, startOfMonth(maxDate))) return true;
			return false;
		}

		if (view === 'week') {
			const weekStart = startOfWeek(today);
			const weekEnd = endOfWeek(today);
			if (minDate && isBefore(weekEnd, minDate)) return true;
			if (maxDate && isAfter(weekStart, maxDate)) return true;
			return false;
		}

		// Strip time component: i.e today at 13:00 with maxDate at 00:00 should not be disabled
		const todayStart = startOfDay(today);
		if (minDate && isBefore(todayStart, startOfDay(minDate))) return true;
		if (maxDate && isAfter(todayStart, startOfDay(maxDate))) return true;
		return false;
	}, [minDate, maxDate, view]);

	return {
		inputValue,
		inputType,
		handlePrevClick,
		handleNextClick,
		handleTodayClick,
		handleDateInputChange,
		createViewChangeHandler,
		viewLabels,
		isPrevDisabled,
		isNextDisabled,
		isTodayDisabled,
		minDate,
		maxDate,
	};
};
